import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatVND } from "@/lib/api";
import { getSourceLabel, getTagLabel } from "@/lib/labels";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Users, Search, Phone, Plus, ArrowUpDown, Tag, MapPin,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Calendar, DollarSign, CircleDollarSign, CheckCircle2, AlertTriangle, Clock, Edit, Trash2,
} from "lucide-react";
import CustomerModal from "@/components/customers/CustomerModal";
import ExportButtons from "@/components/ExportButtons";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const TAG_COLORS = {
  VIP: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Khách quen": "bg-lime-500/20 text-lime-400 border-lime-500/30",
  "Thuê tháng": "bg-sky-500/20 text-sky-400 border-sky-500/30",
  "Nhóm": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  Social: "bg-teal-500/20 text-teal-400 border-teal-500/30",
};

function getTagColor(tag) {
  return TAG_COLORS[tag] || "bg-slate-500/20 text-slate-400 border-slate-500/30";
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [allSources, setAllSources] = useState([]);
  const [search, setSearch] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const PAGE_SIZE = 10;

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("q", search);
      if (filterTag) params.append("tag", filterTag);
      if (filterSource) params.append("source", filterSource);
      params.append("sort_by", sortBy);
      params.append("sort_dir", sortDir);
      params.append("page", page);
      params.append("limit", PAGE_SIZE);
      const { data } = await api.get(`/customers?${params.toString()}`);
      setCustomers(data.customers || []);
      setTotalCustomers(data.total || 0);
      setAllTags(data.all_tags || []);
      setAllSources(data.all_sources || []);
    } catch (err) {
      console.error("Failed to load customers", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterTag, filterSource, sortBy, sortDir, page]);

  useEffect(() => {
    const timer = setTimeout(() => loadCustomers(), 300);
    return () => clearTimeout(timer);
  }, [loadCustomers]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, filterTag, filterSource, sortBy, sortDir]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 text-slate-600" />;
    return sortDir === "asc"
      ? <ChevronUp className="w-3 h-3 text-lime-400" />
      : <ChevronDown className="w-3 h-3 text-lime-400" />;
  };

  const handleAdd = () => {
    setEditingCustomer(null);
    setModalOpen(true);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setModalOpen(true);
  };

  const handleOpenCleanup = async () => {
    setCleanupOpen(true);
    setCleanupLoading(true);
    setCleanupPreview(null);
    try {
      const { data } = await api.get("/customers/phone-cleanup/preview");
      setCleanupPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi tải preview");
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleApplyCleanup = async () => {
    if (!window.confirm(`Xác nhận chạy dọn dẹp: xoá ${cleanupPreview?.total_to_delete || 0} tài khoản trùng + chuẩn hoá ${cleanupPreview?.whitespace_count || 0} SĐT có khoảng trắng?`)) return;
    setCleanupRunning(true);
    try {
      const { data } = await api.post("/customers/phone-cleanup/apply", {
        strip_whitespace: true,
        merge_duplicates: true,
      });
      toast.success(`Hoàn tất: sửa ${data.whitespace_fixed} SĐT, gộp ${data.merged_groups} nhóm, xoá ${data.deleted_accounts} account`);
      setCleanupOpen(false);
      setPage(1);
      await loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi chạy dọn dẹp");
    } finally {
      setCleanupRunning(false);
    }
  };

  const handleTogglePayment = async (e, c) => {
    e.stopPropagation();
    const cycle = { "": "paid", "paid": "unpaid", "unpaid": "overdue", "overdue": "paid" };
    const next = cycle[c.payment_status || ""] || "paid";
    try {
      await api.put(`/customers/${c.id}`, { payment_status: next });
      loadCustomers();
    } catch { /* ignore */ }
  };

  const PaymentIcon = ({ status }) => {
    if (status === "paid") return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
    if (status === "unpaid") return <Clock className="w-4 h-4 text-amber-400" />;
    if (status === "overdue") return <AlertTriangle className="w-4 h-4 text-red-400" />;
    return <CircleDollarSign className="w-4 h-4 text-slate-600" />;
  };

  const paymentLabel = (status) => {
    if (status === "paid") return "Đã TT";
    if (status === "unpaid") return "Chưa TT";
    if (status === "overdue") return "Quá hạn";
    return "—";
  };

  const handleRowClick = (customer) => {
    navigate(`/customers/${customer.id}`);
  };

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight">
            Khách hàng
          </h1>
          <p className="text-slate-400 text-sm mt-1">Quản lý danh sách khách hàng CRM</p>
        </div>
        <div className="flex items-center gap-3">
          <ExportButtons endpoint="/export/customers" label="khach-hang" />
          <Button
            onClick={handleAdd}
            className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold gap-1.5"
            data-testid="add-customer-btn"
          >
            <Plus className="w-4 h-4" />
            Thêm khách hàng
          </Button>
          <Button
            onClick={() => setManageTagsOpen(true)}
            variant="outline"
            className="border-[#334155] text-slate-300 hover:text-lime-400 gap-1.5"
            data-testid="manage-tags-btn"
          >
            <Tag className="w-4 h-4" />
            Nguồn & Tags
          </Button>
          <Button
            onClick={handleOpenCleanup}
            variant="outline"
            className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 gap-1.5"
            data-testid="phone-cleanup-btn"
            title="Chuẩn hoá SĐT + Gộp khách trùng"
          >
            <AlertTriangle className="w-4 h-4" />
            Chuẩn hoá SĐT
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-2 bg-[#1e293b] border border-[#334155] rounded-lg px-3 flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-500 shrink-0" />
          <Input
            placeholder="Tìm theo tên, SĐT, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-0 bg-transparent text-slate-200 placeholder:text-slate-500 focus-visible:ring-0 focus-visible:ring-offset-0 h-9"
            data-testid="customer-search-input"
          />
        </div>
        <Select value={filterTag} onValueChange={(v) => setFilterTag(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px] bg-[#1e293b] border-[#334155] text-slate-300 h-9" data-testid="filter-tag">
            <Tag className="w-3.5 h-3.5 text-slate-500 mr-1.5" />
            <SelectValue placeholder="Tất cả tags" />
          </SelectTrigger>
          <SelectContent className="bg-[#1e293b] border-[#334155]">
            <SelectItem value="all" className="text-slate-300">Tất cả tags</SelectItem>
            {allTags.map(t => (
              <SelectItem key={t} value={t} className="text-slate-300">{getTagLabel(t)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={(v) => setFilterSource(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px] bg-[#1e293b] border-[#334155] text-slate-300 h-9" data-testid="filter-source">
            <MapPin className="w-3.5 h-3.5 text-slate-500 mr-1.5" />
            <SelectValue placeholder="Tất cả nguồn" />
          </SelectTrigger>
          <SelectContent className="bg-[#1e293b] border-[#334155]">
            <SelectItem value="all" className="text-slate-300">Tất cả nguồn</SelectItem>
            {allSources.map(s => (
              <SelectItem key={s} value={s} className="text-slate-300">{getSourceLabel(s)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 text-sm text-slate-400">
        <div className="flex items-center gap-1.5">
          <Users className="w-4 h-4 text-lime-500" />
          <span>{totalCustomers} khách hàng (trang {page}/{Math.ceil(totalCustomers / PAGE_SIZE) || 1})</span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-[#334155] hover:bg-transparent">
              <TableHead className="text-slate-400">
                <button onClick={() => toggleSort("name")} className="flex items-center gap-1 hover:text-slate-200 transition-colors" data-testid="sort-name">
                  Họ tên <SortIcon field="name" />
                </button>
              </TableHead>
              <TableHead className="text-slate-400">SĐT</TableHead>
              <TableHead className="text-slate-400">Hội</TableHead>
              <TableHead className="text-slate-400">Tags</TableHead>
              <TableHead className="text-slate-400">Nguồn</TableHead>
              <TableHead className="text-slate-400">
                <button onClick={() => toggleSort("stats")} className="flex items-center gap-1 hover:text-slate-200 transition-colors">
                  Lần đặt <SortIcon field="stats" />
                </button>
              </TableHead>
              <TableHead className="text-slate-400">Tổng chi</TableHead>
              <TableHead className="text-slate-400">Thanh toán</TableHead>
              <TableHead className="text-slate-400">Lần cuối</TableHead>
              <TableHead className="text-slate-400 w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array(5).fill(0).map((_, i) => (
                <TableRow key={i} className="border-[#334155]">
                  <TableCell colSpan={10}>
                    <div className="h-10 bg-[#334155]/50 rounded animate-pulse" />
                  </TableCell>
                </TableRow>
              ))
            ) : customers.length === 0 ? (
              <TableRow className="border-[#334155]">
                <TableCell colSpan={10} className="text-center py-12 text-slate-500">
                  Không tìm thấy khách hàng
                </TableCell>
              </TableRow>
            ) : (
              customers.map((c) => {
                const isToday = c.created_at && new Date(c.created_at).toDateString() === new Date().toDateString();
                return (
                <TableRow
                  key={c.id}
                  className={`border-[#334155] hover:bg-[#334155]/30 cursor-pointer ${isToday ? "animate-new-customer" : ""}`}
                  onClick={() => handleRowClick(c)}
                  data-testid={`customer-row-${c.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#334155] flex items-center justify-center text-xs font-semibold text-lime-400 shrink-0">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="text-slate-200 font-medium text-sm">{c.name}</span>
                        <div className="text-[11px] text-slate-500">{c.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-slate-300 text-sm">
                      <Phone className="w-3.5 h-3.5 text-slate-500" />
                      {c.phone || "\u2014"}
                    </div>
                  </TableCell>
                  <TableCell>
                    {c.group_memberships && c.group_memberships.length > 0 ? (
                      <div className="flex flex-wrap gap-1" data-testid={`customer-groups-${c.id}`}>
                        {c.group_memberships.map((m) => (
                          <Badge
                            key={m.member_id}
                            className={`text-[10px] px-1.5 py-0 border ${
                              m.is_active
                                ? "bg-purple-500/20 text-purple-400 border-purple-500/30"
                                : "bg-slate-500/15 text-slate-500 border-slate-500/25 line-through"
                            }`}
                            title={`${m.group_name} · ${m.join_date || "—"} → ${m.end_date || "—"} · ${m.is_active ? "Đang hoạt động" : "Hết hạn"}`}
                          >
                            {m.group_name || "(Hội?)"}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[11px] text-slate-600">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(c.tags || []).map(tag => (
                        <Badge key={tag} className={`${getTagColor(tag)} text-[10px] px-1.5 py-0 border`}>
                          {getTagLabel(tag)}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-400 text-sm">{getSourceLabel(c.source) || "\u2014"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Calendar className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-slate-300">{c.stats?.total_bookings || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <DollarSign className="w-3.5 h-3.5 text-lime-500" />
                      <span className="text-lime-400 font-medium">{formatVND(c.stats?.total_spent || 0)}đ</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <button onClick={(e) => handleTogglePayment(e, c)}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors hover:bg-[#334155]/50"
                      title="Click để chuyển trạng thái" data-testid={`toggle-payment-${c.id}`}>
                      <PaymentIcon status={c.payment_status} />
                      <span className={`${c.payment_status === "paid" ? "text-emerald-400" : c.payment_status === "overdue" ? "text-red-400" : c.payment_status === "unpaid" ? "text-amber-400" : "text-slate-500"}`}>
                        {paymentLabel(c.payment_status)}
                      </span>
                    </button>
                  </TableCell>
                  <TableCell className="text-slate-500 text-xs">
                    {c.stats?.last_booking_date
                      ? new Date(c.stats.last_booking_date + "T00:00:00").toLocaleDateString("vi-VN")
                      : "\u2014"}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-400 hover:text-lime-400 text-xs h-7 px-2"
                      onClick={(e) => { e.stopPropagation(); handleEdit(c); }}
                      data-testid={`edit-customer-${c.id}`}
                    >
                      Sửa
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalCustomers > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4" data-testid="customers-pagination">
          <span className="text-xs text-slate-500">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCustomers)} / {totalCustomers}
          </span>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="p-1.5 rounded border border-[#334155] text-slate-400 hover:text-white disabled:opacity-30 transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.ceil(totalCustomers / PAGE_SIZE) }, (_, i) => i + 1)
              .filter(p => p === 1 || p === Math.ceil(totalCustomers / PAGE_SIZE) || Math.abs(p - page) <= 2)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) => p === "..." ? (
                <span key={`dot-${i}`} className="px-1 text-slate-600">...</span>
              ) : (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded text-xs font-medium border transition-colors ${page === p ? "bg-sky-500/20 text-sky-400 border-sky-500/40" : "border-[#334155] text-slate-400 hover:text-white"}`}>
                  {p}
                </button>
              ))}
            <button onClick={() => setPage(p => Math.min(Math.ceil(totalCustomers / PAGE_SIZE), p + 1))} disabled={page >= Math.ceil(totalCustomers / PAGE_SIZE)}
              className="p-1.5 rounded border border-[#334155] text-slate-400 hover:text-white disabled:opacity-30 transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <CustomerModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        customer={editingCustomer}
        allTags={allTags}
        allSources={allSources}
        onSaved={loadCustomers}
      />
      <ManageSourcesTagsDialog
        open={manageTagsOpen}
        onOpenChange={setManageTagsOpen}
        onSaved={loadCustomers}
      />

      {/* Phone Cleanup Dialog */}
      <Dialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="phone-cleanup-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Chuẩn hoá SĐT & Gộp khách trùng
            </DialogTitle>
          </DialogHeader>
          {cleanupLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-xs text-slate-400">Đang quét {totalCustomers.toLocaleString()} khách...</span>
            </div>
          ) : cleanupPreview ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-[#0f172a] border border-slate-500/30 rounded-lg p-3 text-center">
                  <div className="text-[10px] text-slate-500 uppercase">Tổng KH</div>
                  <div className="text-lg font-bold text-slate-200">{cleanupPreview.total_customers}</div>
                </div>
                <div className="bg-[#0f172a] border border-sky-500/30 rounded-lg p-3 text-center">
                  <div className="text-[10px] text-sky-400 uppercase">SĐT có khoảng trắng</div>
                  <div className="text-lg font-bold text-sky-300" data-testid="cleanup-whitespace-count">{cleanupPreview.whitespace_count}</div>
                </div>
                <div className="bg-[#0f172a] border border-amber-500/30 rounded-lg p-3 text-center">
                  <div className="text-[10px] text-amber-400 uppercase">Nhóm trùng SĐT</div>
                  <div className="text-lg font-bold text-amber-300" data-testid="cleanup-dup-groups">{cleanupPreview.total_duplicate_groups}</div>
                  <div className="text-[9px] text-slate-500">sẽ xoá {cleanupPreview.total_to_delete} acc</div>
                </div>
              </div>

              {cleanupPreview.duplicate_groups.length === 0 ? (
                <div className="text-center py-6 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  Không có SĐT trùng. Chỉ cần strip khoảng trắng.
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                  <p className="text-[11px] text-slate-400">Danh sách các nhóm SĐT trùng (hiển thị tối đa 50):</p>
                  {cleanupPreview.duplicate_groups.slice(0, 50).map((g) => (
                    <div key={g.normalized_phone} className="bg-[#0f172a] border border-amber-500/20 rounded-md p-2 text-[11px]" data-testid={`dup-group-${g.normalized_phone}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-amber-400">{g.normalized_phone}</span>
                        <span className="text-slate-500">{g.count} tài khoản</span>
                      </div>
                      <div className="space-y-0.5">
                        <div className="text-emerald-400">
                          ✓ Giữ: <span className="font-semibold">{g.keeper.name}</span>
                          <span className="text-slate-500"> ({g.keeper.email}) · {g.keeper._booking_count} booking</span>
                        </div>
                        {g.losers.map((l) => (
                          <div key={l.id} className="text-red-400/70 line-through">
                            ✗ Xoá: {l.name} <span className="text-slate-600">({l.email}) · {l._booking_count} booking</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 pt-2 border-t border-[#334155]">
                <Button variant="ghost" onClick={() => setCleanupOpen(false)} className="text-slate-400" data-testid="cleanup-cancel-btn">
                  Huỷ
                </Button>
                <Button
                  onClick={handleApplyCleanup}
                  disabled={cleanupRunning || (cleanupPreview.whitespace_count === 0 && cleanupPreview.total_duplicate_groups === 0)}
                  className="bg-amber-500 hover:bg-amber-600 text-[#0f172a] font-semibold ml-auto"
                  data-testid="cleanup-apply-btn"
                >
                  {cleanupRunning ? "Đang xử lý..." : "Chạy dọn dẹp"}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Manage Sources & Tags Dialog ---
function ManageSourcesTagsDialog({ open, onOpenChange, onSaved }) {
  const [tab, setTab] = useState("sources");
  const [sources, setSources] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null); // {type, name}
  const [mergeInto, setMergeInto] = useState("");
  const [renameItem, setRenameItem] = useState(null);
  const [newName, setNewName] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [srcRes, tagRes] = await Promise.all([
        api.get("/customers/sources"),
        api.get("/customers/tags"),
      ]);
      setSources(srcRes.data.sources || []);
      setTags(tagRes.data.tags || []);
    } catch { toast.error("Lỗi tải dữ liệu"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      const endpoint = deleteItem.type === "source" ? "/customers/sources" : "/customers/tags";
      await api.delete(endpoint, { data: { value: deleteItem.name, merge_into: mergeInto } });
      toast.success(mergeInto ? `Đã gộp vào "${mergeInto}"` : "Đã xoá");
      setDeleteItem(null); setMergeInto("");
      fetchData(); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleRename = async () => {
    if (!renameItem || !newName.trim()) return;
    try {
      const endpoint = renameItem.type === "source" ? "/customers/sources" : "/customers/tags";
      await api.post(endpoint, { old_value: renameItem.name, new_value: newName.trim() });
      toast.success("Đã đổi tên");
      setRenameItem(null); setNewName("");
      fetchData(); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const currentList = tab === "sources" ? sources : tags;
  const otherOptions = currentList.filter(i => i.name !== deleteItem?.name).map(i => i.name);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md max-h-[80vh] overflow-y-auto" data-testid="manage-tags-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC]">Quản lý Nguồn & Tags</DialogTitle>
        </DialogHeader>
        {/* Tabs */}
        <div className="flex gap-1 bg-[#0f172a] rounded-lg p-1">
          <button onClick={() => setTab("sources")} className={`flex-1 py-1.5 rounded text-xs font-medium ${tab === "sources" ? "bg-[#1e293b] text-lime-400" : "text-slate-400"}`}>Nguồn ({sources.length})</button>
          <button onClick={() => setTab("tags")} className={`flex-1 py-1.5 rounded text-xs font-medium ${tab === "tags" ? "bg-[#1e293b] text-lime-400" : "text-slate-400"}`}>Tags ({tags.length})</button>
        </div>
        {/* List */}
        <div className="space-y-1 max-h-[300px] overflow-y-auto">
          {loading ? <p className="text-xs text-slate-500 text-center py-4">Đang tải...</p> :
          currentList.length === 0 ? <p className="text-xs text-slate-500 text-center py-4">Không có {tab === "sources" ? "nguồn" : "tag"}</p> :
          currentList.map(item => (
            <div key={item.name} className="flex items-center justify-between bg-[#0f172a] rounded-lg px-3 py-2 group">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-200">{item.name}</span>
                <span className="text-[10px] text-slate-500">{item.count} KH</span>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => { setRenameItem({ type: tab === "sources" ? "source" : "tag", name: item.name }); setNewName(item.name); }}
                  className="text-slate-400 hover:text-lime-400 p-1"><Edit className="w-3 h-3" /></button>
                <button onClick={() => { setDeleteItem({ type: tab === "sources" ? "source" : "tag", name: item.name }); setMergeInto(""); }}
                  className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>

        {/* Delete confirmation */}
        {deleteItem && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 space-y-2">
            <p className="text-xs text-red-400 font-medium">Xoá "{deleteItem.name}"?</p>
            <p className="text-[10px] text-slate-400">Gộp với {deleteItem.type === "source" ? "nguồn" : "tag"} khác? (tuỳ chọn)</p>
            <select value={mergeInto} onChange={e => setMergeInto(e.target.value)}
              className="w-full bg-[#0f172a] border border-[#334155] rounded px-2 py-1.5 text-xs text-slate-200">
              <option value="">Không gộp — xoá trắng</option>
              {otherOptions.map(o => <option key={o} value={o}>Gộp vào: {o}</option>)}
            </select>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDeleteItem(null)} className="text-slate-400 text-xs">Huỷ</Button>
              <Button size="sm" onClick={handleDelete} className="bg-red-500 text-white text-xs">
                {mergeInto ? `Gộp & Xoá` : `Xoá`}
              </Button>
            </div>
          </div>
        )}

        {/* Rename */}
        {renameItem && (
          <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-3 space-y-2">
            <p className="text-xs text-lime-400 font-medium">Đổi tên "{renameItem.name}"</p>
            <Input value={newName} onChange={e => setNewName(e.target.value)} className="bg-[#0f172a] border-[#334155] text-slate-50 h-8 text-xs" />
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setRenameItem(null)} className="text-slate-400 text-xs">Huỷ</Button>
              <Button size="sm" onClick={handleRename} className="bg-lime-500 text-[#0f172a] text-xs font-bold">Đổi tên</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
